import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Group, Stack } from "@mantine/core";

import { format } from "date-fns";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterNoteDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterNoteDifference";
import { EncounterSummaryDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterSummaryDifference";
import DxDiagnosesVersionDiff from "@/encounters/components/DxPlan/DxDiagnosesVersionDiff";
import ProceduresAmendmentDiff from "@/encounters/components/ProceduresAmendmentDiff";
import { FilesDiff } from "@/encounters/components/shared/Files";
import { NewbornPostpartumEncounterQuestionsLinkIds } from "@/encounters/constants/newborn-postpartum";
import { useEncounterDetailVersionPair } from "@/encounters/hooks/useEncounterDetailVersionPair";
import { useLoadBirthDateTime } from "@/encounters/hooks/useLoadBirthDateTime";
import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";
import NewbornSystemsDiff from "@/newborn-systems/NewbornSystemsDiff";
import NotFound from "@/NotFound";

import InputsAndOutputsDiff from "./components/InputsAndOutputsDiff";
import MedicationsAndTherapiesDiff from "./components/MedicationsAndTherapiesDiff";
import NewbornPostpartumPhysicalExamsDiff from "./components/NewbornPostpartumPhysicalExamsDiff";
import NewbornPPDiffHeader from "./components/NewbornPPDiffHeader";
import NewbornPPEncounterDiff from "./components/NewbornPPEncounterDiff";
import NewbornProceduresDiff from "./components/NewbornProceduresDiff";
import SizeDiff from "./components/SizeDiff";
import StateOfNewbornDiff from "./components/StateOfNewbornDiff";
import VitalsSignDiff from "./components/VitalsSignDiff";

const NewbornPPVersionDiff = () => {
  const { encounterId, versionId, clientId } = useParams<{
    encounterId: string;
    versionId: string;
    clientId: string;
  }>();

  const [encounter, setEncounter] = useState<Encounter>();
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();
  const [amendmentHistory, setAmendmentHistory] = useState<QuestionnaireResponse[]>();
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(0);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const navigate = useNavigate();
  const medplum = useMedplum();

  useLoadBirthDateTime(clientId);

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        if (!versionId || !encounterId) return;

        const encounter = await medplum.readResource("Encounter", encounterId);
        if (!encounter) return;

        setEncounter(encounter);

        const foundAmendmentHistory = await fetchAmendmentHistory(medplum, encounter);
        if (!foundAmendmentHistory) return;
        const currentHistoryVersionIndex = foundAmendmentHistory.findIndex(
          (questionnaireResponse) => questionnaireResponse.meta?.versionId === versionId,
        );

        if (currentHistoryVersionIndex === -1) return;

        setCurrentVersionIndex(currentHistoryVersionIndex);
        setCurrentVersion(foundAmendmentHistory[currentHistoryVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentHistoryVersionIndex + 1]);
        setAmendmentHistory(foundAmendmentHistory);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [versionId, encounterId, medplum]);

  const {
    currentEncounter,
    previousEncounter,
    isLoading: isEncounterPairLoading,
  } = useEncounterDetailVersionPair(encounterId, amendmentHistory, currentVersionIndex);

  if (isFetchingInitialData || !encounter || isEncounterPairLoading) return <Loading />;

  if ((!currentVersion || !previousVersion) && !isFetchingInitialData) return <NotFound />;

  const lastAmendedBy = currentVersion?.meta?.author?.display;
  const lastAmended = currentVersion?.meta?.lastUpdated;

  return (
    <Stack py="md">
      <NewbornPPDiffHeader questionnaireResponse={currentVersion!} />
      <NewbornPPEncounterDiff
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        currentEncounter={currentEncounter}
        previousEncounter={previousEncounter}
      />
      <DxDiagnosesVersionDiff currentVersion={currentVersion} previousVersion={previousVersion} />
      <EncounterNoteDifference
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        questionLinkId={NewbornPostpartumEncounterQuestionsLinkIds.EncounterNote}
      />
      <EncounterSummaryDifference
        currentVersion={currentVersion}
        previousVersion={previousVersion}
      />
      <StateOfNewbornDiff currentVersionIndex={currentVersionIndex} />
      <VitalsSignDiff currentVersionIndex={currentVersionIndex} />
      <SizeDiff currentVersionIndex={currentVersionIndex} />
      <InputsAndOutputsDiff currentVersionIndex={currentVersionIndex} />
      <NewbornSystemsDiff versionId={versionId} currentVersionIndex={currentVersionIndex} />
      <NewbornProceduresDiff currentVersionIndex={currentVersionIndex} />
      <ProceduresAmendmentDiff encounter={encounter} versionId={versionId} />
      <MedicationsAndTherapiesDiff currentVersionIndex={currentVersionIndex} />
      <NewbornPostpartumPhysicalExamsDiff
        versionId={versionId}
        currentVersionIndex={currentVersionIndex}
      />
      <FilesDiff currentVersionIndex={currentVersionIndex} />
      <Group justify="flex-end">
        <Stack align="flex-end">
          <Button
            variant="outline"
            data-testid="exit-amendment-view-button"
            onClick={() =>
              navigate(`/clients/${clientId}/encounters/${encounterId}`, {
                preventScrollReset: true,
              })
            }
          >
            Exit Amendment View
          </Button>
          {lastAmendedBy && lastAmended && (
            <TextWrapper>
              Last amended by {lastAmendedBy} - {format(lastAmended, "MM/dd/yyyy")}
            </TextWrapper>
          )}
        </Stack>
      </Group>
    </Stack>
  );
};

export default NewbornPPVersionDiff;
